import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const DeviceUsersTab = ({ deviceId }) => {
  const { user } = useAuth();
  const [accesses, setAccesses] = useState([]);
  const [allDeviceUsers, setAllDeviceUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(null);

  const [formData, setFormData] = useState({
    device_user_id: null,
    access_type: 'local',
    access_level: 'read_only',
    description: '',
    valid_from: '',
    valid_until: '',
    is_active: true
  });

  useEffect(() => {
    fetchAccesses();
    fetchAllDeviceUsers();
  }, [deviceId]);

  const fetchAccesses = async () => {
    try {
      const response = await axios.get(`/device-users/accesses/device/${deviceId}`);
      setAccesses(response.data.accesses || []);
    } catch (err) {
      console.error('Error fetching accesses:', err);
    }
  };

  const fetchAllDeviceUsers = async () => {
    try {
      const response = await axios.get('/device-users/', { params: { limit: 1000, is_active: true } });
      setAllDeviceUsers(response.data);
    } catch (err) {
      console.error('Error fetching device users:', err);
    }
  };

  const handleOpenDialog = (accessToEdit = null) => {
    if (accessToEdit) {
      setEditMode(true);
      setSelectedAccess(accessToEdit);
      setFormData({
        device_user_id: accessToEdit.device_user_id,
        access_type: accessToEdit.access_type,
        access_level: accessToEdit.access_level,
        description: accessToEdit.description || '',
        valid_from: accessToEdit.valid_from ? accessToEdit.valid_from.split('T')[0] : '',
        valid_until: accessToEdit.valid_until ? accessToEdit.valid_until.split('T')[0] : '',
        is_active: accessToEdit.is_active ?? true
      });
    } else {
      setEditMode(false);
      setSelectedAccess(null);
      setFormData({
        device_user_id: null,
        access_type: 'local',
        access_level: 'read_only',
        description: '',
        valid_from: '',
        valid_until: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedAccess(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };

      if (editMode) {
        await axios.put(`/device-users/accesses/${selectedAccess.id}`, payload);
      } else {
        await axios.post('/device-users/accesses/', {
          ...payload,
          device_id: deviceId
        });
      }
      handleCloseDialog();
      fetchAccesses();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
      console.error(err);
    }
  };

  const handleDelete = async (accessId) => {
    if (!window.confirm('Отозвать этот доступ?')) return;

    try {
      await axios.delete(`/device-users/accesses/${accessId}`);
      fetchAccesses();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
      console.error(err);
    }
  };

  const getAccessTypeLabel = (type) => {
    const labels = {
      local: 'Локальный',
      remote: 'Удаленный',
      console: 'Консольный',
      physical: 'Физический',
      administrative: 'Административный'
    };
    return labels[type] || type;
  };

  const getAccessLevelLabel = (level) => {
    const labels = {
      read_only: 'Просмотр',
      operator: 'Оператор',
      admin: 'Администратор',
      full_access: 'Полный доступ'
    };
    return labels[level] || level;
  };

  const getAccessLevelColor = (level) => {
    const colors = {
      read_only: 'default',
      operator: 'info',
      admin: 'warning',
      full_access: 'error'
    };
    return colors[level] || 'default';
  };

  const getAccessTypeColor = (type) => {
    const colors = {
      local: 'default',
      remote: 'primary',
      console: 'secondary',
      physical: 'info',
      administrative: 'error'
    };
    return colors[type] || 'default';
  };

  const canManage = user?.is_superuser || user?.permissions?.includes('device_users.manage');
  const canView = user?.is_superuser || user?.permissions?.includes('device_users.view');

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">У вас нет прав для просмотра пользователей устройств</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          ({accesses.length})
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Добавить пользователя
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {accesses.length === 0 ? (
        <Alert severity="info">Нет пользователей с доступом к этому устройству</Alert>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
              {accesses.map((access, index) => (
                <React.Fragment key={access.id}>
                  {index > 0 && <Divider sx={{ my: 2 }} />}
                  <ListItem
                    sx={{ px: 0 }}
                    secondaryAction={
                      canManage && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            edge="end"
                            onClick={() => handleOpenDialog(access)}
                            size="small"
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDelete(access.id)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="subtitle1" fontWeight="500">
                            {access.device_user?.full_name || `Пользователь ID: ${access.device_user_id}`}
                          </Typography>
                          {!access.is_active && (
                            <Chip label="Неактивен" size="small" color="default" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {access.device_user?.email && (
                            <Typography variant="body2" color="text.secondary">
                              {access.device_user.email}
                            </Typography>
                          )}
                          {access.device_user?.department && (
                            <Typography variant="body2" color="text.secondary">
                              {access.device_user.department}
                              {access.device_user.position && ` • ${access.device_user.position}`}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            <Chip
                              label={`Тип: ${getAccessTypeLabel(access.access_type)}`}
                              size="small"
                              color={getAccessTypeColor(access.access_type)}
                              variant="outlined"
                            />
                            <Chip
                              label={`Уровень: ${getAccessLevelLabel(access.access_level)}`}
                              size="small"
                              color={getAccessLevelColor(access.access_level)}
                            />
                          </Box>
                          {access.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {access.description}
                            </Typography>
                          )}
                          {(access.valid_from || access.valid_until) && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              {access.valid_from && `Действует с: ${new Date(access.valid_from).toLocaleDateString()}`}
                              {access.valid_from && access.valid_until && ' | '}
                              {access.valid_until && `До: ${new Date(access.valid_until).toLocaleDateString()}`}
                            </Typography>
                          )}
                          {access.granted_by && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Предоставил: {access.granted_by.email}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
        </List>
      )}

      {/* Add/Edit Access Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Редактировать доступ' : 'Предоставить доступ'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={allDeviceUsers}
              getOptionLabel={(option) => `${option.full_name}${option.department ? ` (${option.department})` : ''}`}
              value={allDeviceUsers.find(u => u.id === formData.device_user_id) || null}
              onChange={(e, newValue) => {
                setFormData({ ...formData, device_user_id: newValue?.id || null });
              }}
              disabled={editMode}
              renderInput={(params) => (
                <TextField {...params} label="Пользователь" required />
              )}
            />

            <TextField
              label="Тип доступа"
              fullWidth
              required
              select
              value={formData.access_type}
              onChange={(e) => setFormData({ ...formData, access_type: e.target.value })}
            >
              <MenuItem value="local">Локальный</MenuItem>
              <MenuItem value="remote">Удаленный (SSH, RDP)</MenuItem>
              <MenuItem value="console">Консольный (COM-порт)</MenuItem>
              <MenuItem value="physical">Физический (в серверную)</MenuItem>
              <MenuItem value="administrative">Административный (все виды)</MenuItem>
            </TextField>

            <TextField
              label="Уровень доступа"
              fullWidth
              required
              select
              value={formData.access_level}
              onChange={(e) => setFormData({ ...formData, access_level: e.target.value })}
            >
              <MenuItem value="read_only">Только просмотр</MenuItem>
              <MenuItem value="operator">Оператор (базовые операции)</MenuItem>
              <MenuItem value="admin">Администратор (настройка)</MenuItem>
              <MenuItem value="full_access">Полный доступ</MenuItem>
            </TextField>

            <TextField
              label="Описание"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Дополнительная информация о доступе..."
            />

            <TextField
              label="Действует с"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.valid_from}
              onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
            />

            <TextField
              label="Действует до"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.valid_until}
              onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Доступ активен"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.device_user_id}
          >
            {editMode ? 'Сохранить' : 'Предоставить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceUsersTab;
